import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scan, Type, Check, X, Package, MapPin } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { CameraScanner } from '../components/scanner/CameraScanner';
import { usePickingStore } from '../stores/pickingStore';
import { useAppStore } from '../stores/appStore';

export const ScannerPage: React.FC = () => {
  const [scanMode, setScanMode] = useState<'camera' | 'manual'>('camera');
  const [manualBarcode, setManualBarcode] = useState('');
  const { 
    startScanning, 
    stopScanning, 
    scanning, 
    scanResult, 
    processScan,
    setScanResult 
  } = usePickingStore();
  const { addNotification } = useAppStore();

  const handleCameraScan = (barcode: string) => {
    processScan(barcode);
    stopScanning();
    
    if (scanResult?.success) {
      addNotification({
        type: 'success',
        title: 'Scan Successful',
        message: `Found ${scanResult.productName}`
      });
    }
  };

  const handleManualScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      processScan(manualBarcode.trim());
      setManualBarcode('');
      
      addNotification({
        type: 'info',
        title: 'Manual Scan',
        message: 'Barcode entered manually',
        priority: 'low'
      });
    }
  };

  const clearResults = () => {
    setScanResult(null);
  };

  return (
    <div className="p-4 pb-20 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Barcode Scanner
        </h1>
        <p className="text-gray-600">
          Scan items to verify and locate products
        </p>
      </motion.div>

      {/* Scan Mode Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setScanMode('camera')}
            className={`
              flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all
              ${scanMode === 'camera'
                ? 'bg-white text-blue-800 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            <Scan className="w-4 h-4 inline mr-2" />
            Camera Scan
          </button>
          
          <button
            onClick={() => setScanMode('manual')}
            className={`
              flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all
              ${scanMode === 'manual'
                ? 'bg-white text-blue-800 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            <Type className="w-4 h-4 inline mr-2" />
            Manual Entry
          </button>
        </div>
      </motion.div>

      {/* Scanner Interface */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        {scanMode === 'camera' ? (
          <Card className="text-center">
            <div className="py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scan className="w-10 h-10 text-saavi-600" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Camera Scanner
              </h3>
              
              <p className="text-gray-600 mb-6">
                Use your device camera to scan barcodes
              </p>
              
              <Button
                onClick={() => startScanning()}
                size="lg"
                variant="primary"
                icon={<Scan className="w-5 h-5 text-white" />}
              >
                Start Camera Scan
              </Button>
            </div>
          </Card>
        ) : (
          <Card>
            <form onSubmit={handleManualScan} className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Manual Entry
                </h3>
                <p className="text-gray-600 mb-4">
                  Enter the barcode manually if camera scanning isn't available
                </p>
              </div>
              
              <Input
                label="Barcode"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
                placeholder="Enter barcode or SKU"
                icon={<Type className="w-5 h-5 text-saavi-600" />}
              />
              
              <Button
                type="submit"
                disabled={!manualBarcode.trim()}
                variant="primary"
                className="w-full"
                size="lg"
              >
                Submit Barcode
              </Button>
            </form>
          </Card>
        )}
      </motion.div>

      {/* Scan Results */}
      {scanResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Scan Results
              </h3>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={clearResults}
                icon={<X className="w-4 h-4 text-saavi-600" />}
              />
            </div>
            
            <div className={`
              flex items-center space-x-3 p-4 rounded-lg border-2
              ${scanResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
              }
            `}>
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${scanResult.success 
                  ? 'bg-green-100' 
                  : 'bg-red-100'
                }
              `}>
                {scanResult.success ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>
              
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {scanResult.barcode}
                </p>
                <p className={`
                  text-sm
                  ${scanResult.success ? 'text-green-800' : 'text-red-800'}
                `}>
                  {scanResult.message}
                </p>
              </div>
            </div>
            
            {scanResult.success && scanResult.productName && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-saavi-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-saavi-600" />
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {scanResult.productName}
                    </h4>
                    <p className="text-sm text-gray-600">
                      SKU: {scanResult.productSku}
                    </p>
                    
                    {scanResult.binLocation && (
                      <p className="text-sm text-gray-600 mt-1">
                        {scanResult.binLocation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => setScanMode('camera')}
              className="justify-start"
            >
              <Scan className="w-4 h-4 mr-2" />
              Camera Scan
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setScanMode('manual')}
              className="justify-start"
            >
              <Type className="w-4 h-4 mr-2" />
              Manual Entry
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Camera Scanner Modal */}
      <CameraScanner
        isActive={scanning}
        onScan={handleCameraScan}
        onClose={() => stopScanning()}
      />
    </div>
  );
};